import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, Input } from '../../../components/ui';
import { FileText, ChevronDown, ChevronRight } from 'lucide-react';
import { fetchAPI } from '../../../services/apiClient';

interface Movimiento {
  id: string;
  local_id: string;
  local_nombre: string;
  tipo: string;
  monto: string;
  referencia: string;
  fecha: string;
  saldo_resultante: string;
  detalle: {
    vendido?: string;
    premios?: string;
    comision?: string;
    sorteos?: { sorteo: string; vendido: string; premios: string; comision: string }[];
    movimientos_consolidados?: number;
    fecha_desde?: string;
    fecha_hasta?: string;
  } | null;
  created_by: string;
  created_at: string;
}

interface MovimientosPaginados {
  items: Movimiento[];
  total_count: number;
  pagina: number;
  por_pagina: number;
}

const TIPO_LABELS: Record<string, string> = {
  LIQUIDACION_DIA: 'Liquidación',
  CIERRE_CONSOLIDADO: 'Cierre',
  ABONO: 'Abono',
  PAGO: 'Pago',
};

const TIPO_BADGE: Record<string, 'primary' | 'success' | 'warning' | 'danger' | 'neutral'> = {
  LIQUIDACION_DIA: 'primary',
  CIERRE_CONSOLIDADO: 'neutral',
  ABONO: 'success',
  PAGO: 'warning',
};

const formatMonto = (val: string) => {
  const n = Number(val);
  if (isNaN(n)) return val;
  return n.toLocaleString('es-CR', { minimumFractionDigits: 0 });
};

export const LocalHistoricoPage: React.FC = () => {
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [movimientos, setMovimientos] = useState<Movimiento[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [pagina, setPagina] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const porPagina = 50;

  const cargarMovimientos = async (pag: number = pagina) => {
    setLoading(true);
    setError('');
    try {
      let url = `/saldos/movimientos?pagina=${pag}&por_pagina=${porPagina}`;
      if (fechaDesde) url += `&fecha_desde=${fechaDesde}`;
      if (fechaHasta) url += `&fecha_hasta=${fechaHasta}`;
      const data = await fetchAPI<MovimientosPaginados>(url);
      setMovimientos(data.items);
      setTotalCount(data.total_count);
      setPagina(data.pagina);
    } catch (e: any) {
      setError(e.message || 'Error al cargar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarMovimientos(1);
  }, []);

  const buscar = () => {
    setPagina(1);
    cargarMovimientos(1);
  };

  const irPagina = (pag: number) => {
    cargarMovimientos(pag);
  };

  const totalPaginas = Math.max(1, Math.ceil(totalCount / porPagina));

  const toggleRow = (id: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Historial de Movimientos</h1>
        <p className="text-gray-500">Liquidaciones, abonos, pagos y cierres de tu local</p>
      </div>

      {/* Filtros */}
      <Card>
        <div className="flex flex-col md:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Desde</label>
            <Input
              type="date"
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Hasta</label>
            <Input
              type="date"
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
            />
          </div>
          <Button variant="primary" onClick={buscar} disabled={loading}>
            {loading ? 'Cargando...' : 'Buscar'}
          </Button>
        </div>
      </Card>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>
      )}

      {/* Tabla */}
      <Card padding="none">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Cargando...</div>
        ) : movimientos.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Sin movimientos</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700 w-8"></th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Fecha</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-gray-700">Tipo</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Monto</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Saldo</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Referencia</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {movimientos.map(m => {
                  const isExpanded = expandedRows.has(m.id);
                  const hasDetail = m.detalle && (m.detalle.vendido || m.detalle.movimientos_consolidados);
                  return (
                    <React.Fragment key={m.id}>
                      <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => hasDetail && toggleRow(m.id)}>
                        <td className="px-4 py-3">
                          {hasDetail && (
                            isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />
                          )}
                        </td>
                        <td className="px-4 py-3">{m.fecha}</td>
                        <td className="px-4 py-3 text-center">
                          <Badge variant={TIPO_BADGE[m.tipo] || 'neutral'}>
                            {TIPO_LABELS[m.tipo] || m.tipo}
                          </Badge>
                        </td>
                        <td className={`px-4 py-3 text-right font-mono font-medium ${Number(m.monto) >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {Number(m.monto) >= 0 ? '+' : ''}{formatMonto(m.monto)}
                        </td>
                        <td className={`px-4 py-3 text-right font-bold ${Number(m.saldo_resultante) > 0 ? 'text-red-600' : Number(m.saldo_resultante) < 0 ? 'text-green-600' : 'text-gray-600'}`}>
                          {formatMonto(m.saldo_resultante)}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">{m.referencia}</td>
                      </tr>
                      {isExpanded && m.detalle && (
                        <tr>
                          <td colSpan={6} className="px-8 py-3 bg-gray-50">
                            {m.detalle.vendido && (
                              <>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-2">
                                  <div>
                                    <p className="text-xs text-gray-500">Ventas</p>
                                    <p className="font-medium">{formatMonto(m.detalle.vendido)}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500">Premios</p>
                                    <p className="font-medium">-{formatMonto(m.detalle.premios || '0')}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500">Comisiones</p>
                                    <p className="font-medium">-{formatMonto(m.detalle.comision || '0')}</p>
                                  </div>
                                </div>
                                {m.detalle.sorteos && m.detalle.sorteos.length > 0 && (
                                  <table className="w-full text-xs mt-2">
                                    <thead>
                                      <tr className="text-gray-500 border-b">
                                        <th className="text-left py-1">Sorteo</th>
                                        <th className="text-right py-1">Vendido</th>
                                        <th className="text-right py-1">Premios</th>
                                        <th className="text-right py-1">Comision</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {m.detalle.sorteos.map((s, i) => (
                                        <tr key={i}>
                                          <td className="py-1">{s.sorteo}</td>
                                          <td className="text-right py-1">{formatMonto(s.vendido)}</td>
                                          <td className="text-right py-1">{formatMonto(s.premios)}</td>
                                          <td className="text-right py-1">{formatMonto(s.comision)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </>
                            )}
                            {m.detalle.movimientos_consolidados && (
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <p className="text-xs text-gray-500">Movimientos consolidados</p>
                                  <p className="font-medium">{m.detalle.movimientos_consolidados}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500">Rango</p>
                                  <p className="font-medium">{m.detalle.fecha_desde} a {m.detalle.fecha_hasta}</p>
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Paginación */}
      {totalCount > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <p className="text-sm text-gray-500">
            {totalCount} movimiento{totalCount !== 1 ? 's' : ''} — Página {pagina} de {totalPaginas}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagina <= 1 || loading}
              onClick={() => irPagina(pagina - 1)}
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={pagina >= totalPaginas || loading}
              onClick={() => irPagina(pagina + 1)}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocalHistoricoPage;
